import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ExternalLink,
    Eye,
    Globe2,
    LayoutDashboard,
    Rocket,
    RefreshCw,
    Search,
    SlidersHorizontal
} from "lucide-react";
import PageShell from "../components/layout/PageShell";
import {
    getCrmProjects,
    getLeads,
    publishCrmProject,
    redeployProject
} from "../services/crmService";

import {
    isPlatformUser,
    isAgencyUser
} from "../services/authService";

import "./project.css";

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.shrianshnexus.in";

const PUBLIC_GENERATOR_URL =
    process.env.REACT_APP_PUBLIC_GENERATOR_URL || "https://shrianshnexus.in/generate";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function getErrorMessage(err, fallback = "Failed to load projects.") {
    const apiError = err?.response?.data;

    if (typeof apiError === "string") return apiError;
    if (apiError?.message) return apiError.message;
    if (apiError?.title) return apiError.title;
    if (err?.message) return err.message;

    return fallback;
}

function getProjectId(project) {
    return String(project?.projectId || project?.id || project?.ProjectId || project?.Id || "").trim();
}

function getProjectName(project) {
    return (
        project?.projectName ||
        project?.businessName ||
        project?.name ||
        "Untitled Project"
    );
}

function getProjectIndustry(project) {
    const industry = project?.industry || "Unknown";
    const subIndustry = project?.subIndustry || project?.pattern || "";

    return {
        industry,
        subIndustry
    };
}

function getProjectLiveUrl(project) {
    return project?.liveUrl || project?.publishedUrl || project?.url || "";
}

function getProjectPreviewUrl(project) {
    const projectId = getProjectId(project);

    return (
        project?.previewDocumentUrl ||
        project?.previewUrl ||
        (projectId
            ? `${API_BASE_URL}/api/website/${projectId}/preview-document?v=A&r=${Date.now()}`
            : "")
    );
}

function getProjectLeadCount(project) {
    return Number(
        project?.leadCount ??
        project?.leadsCount ??
        project?.totalLeads ??
        project?.newLeadCount ??
        0
    );
}

function getLeadProjectId(lead) {
    return String(
        lead?.projectId ||
        lead?.projectID ||
        lead?.project?.id ||
        lead?.project?.projectId ||
        lead?.websiteProjectId ||
        ""
    ).trim();
}

function buildLeadCountMap(leads = []) {
    const map = {};

    leads.forEach((lead) => {
        const projectId = getLeadProjectId(lead);

        if (!projectId) return;

        map[projectId] = (map[projectId] || 0) + 1;
    });

    return map;
}

function getProjectLeadCountFromMap(project, leadCountMap = {}) {
    const projectId = String(getProjectId(project) || "").trim();

    if (projectId && leadCountMap[projectId] !== undefined) {
        return Number(leadCountMap[projectId] || 0);
    }

    return getProjectLeadCount(project);
}

function getProjectLastUpdated(project) {
    return (
        project?.updatedAtUtc ||
        project?.lastUpdatedAtUtc ||
        project?.publishedAtUtc ||
        project?.createdAtUtc ||
        project?.createdAt ||
        ""
    );
}

function formatDateTime(value) {
    if (!value) return "-";

    try {
        return new Date(value).toLocaleString();
    } catch {
        return "-";
    }
}

function getDisplayStatus(project) {
    const raw = String(
        project?.status ||
        project?.generationStatus ||
        project?.previewStatus ||
        "Draft"
    ).trim();

    const normalized = raw.toLowerCase();

    if (normalized.includes("failed") || normalized.includes("error")) {
        return "Failed";
    }

    if (getProjectLiveUrl(project) || project?.isPublished) {
        return "Published";
    }

    if (
        normalized.includes("generated") ||
        normalized.includes("ready") ||
        project?.generatedPagesJson ||
        project?.aiResponseJson
    ) {
        return "Generated";
    }

    if (
        normalized.includes("submitted") ||
        normalized.includes("generating") ||
        normalized.includes("pending") ||
        normalized.includes("queued") ||
        normalized.includes("processing")
    ) {
        return "Generating";
    }

    return raw || "Draft";
}

function getStatusClass(status = "") {
    const value = String(status).toLowerCase();

    if (value.includes("published") || value.includes("live")) return "live";
    if (value.includes("generated")) return "generated";
    if (value.includes("failed")) return "failed";
    if (value.includes("generating") || value.includes("submitted") || value.includes("pending")) return "working";

    return "draft";
}

function isPublishable(project) {
    const status = getDisplayStatus(project).toLowerCase();

    return (
        !getProjectLiveUrl(project) &&
        (
            status.includes("generated") ||
            project?.generatedPagesJson ||
            project?.aiResponseJson
        )
    );
}

function isUpdatable(project) {
    return Boolean(getProjectLiveUrl(project));
}

function normalizeText(value) {
    return String(value || "").trim().toLowerCase();
}

function getProjectOwnerLabel(project) {
    const accountName =
        project?.accountName ||
        project?.AccountName ||
        project?.clientName ||
        project?.ClientName ||
        "";

    const accountType =
        project?.accountType ||
        project?.AccountType ||
        "";

    const parentName =
        project?.parentAccountName ||
        project?.ParentAccountName ||
        "";

    if (!accountName) {
        return "Unassigned";
    }

    if (String(accountType).toLowerCase() === "agency") {
        return `${accountName} · Agency workspace`;
    }

    if (parentName) {
        return `${accountName} · Client under ${parentName}`;
    }

    return `${accountName} · Direct client`;
}

function getProjectOwnerType(project) {
    const accountType = String(project?.accountType || project?.AccountType || "").toLowerCase();
    const parentName = project?.parentAccountName || project?.ParentAccountName;

    if (!accountType) return "unassigned";
    if (accountType === "agency") return "agency";
    if (accountType === "client" && parentName) return "agency-client";
    if (accountType === "client") return "direct-client";

    return accountType;
}

function getProjectAgencyId(project) {
    const accountType = String(project?.accountType || project?.AccountType || "").toLowerCase();

    if (project?.parentAccountId || project?.ParentAccountId) {
        return String(project?.parentAccountId || project?.ParentAccountId);
    }

    if (accountType === "agency" && (project?.accountId || project?.AccountId)) {
        return String(project?.accountId || project?.AccountId);
    }

    return "";
}

function ProjectEmptyState({ onRefresh, loading }) {
    const sessionRaw = localStorage.getItem("nexus_session");

    let session = null;

    try {
        session = sessionRaw ? JSON.parse(sessionRaw) : null;
    } catch {
        session = null;
    }

    const isSuperAdmin = !!session?.isSuperAdmin;

    const role =
        session?.role ||
        (isSuperAdmin ? "SuperAdmin" : "AccountOwner");

    const isAgency =
        role === "AgencyOwner" ||
        role === "AgencyAdmin" ||
        role === "AgencySupport" ||
        role === "AgencyViewer";

    let title = "No projects found";
    let description =
        "Generated websites assigned to your account will appear here.";

    if (isSuperAdmin) {
        description =
            "No generated projects are currently available on the platform.";
    } else if (isAgency) {
        title = "No client projects assigned";
        description =
            "Projects assigned to your agency clients will appear here once websites are connected.";
    } else {
        title = "No workspace projects";
        description =
            "Assigned websites will appear here once your workspace receives access.";
    }

    return (
        <div className="projects-empty-state">
            <div className="projects-empty-icon">🌐</div>

            <h3>{title}</h3>

            <p>{description}</p>

            <div className="projects-empty-actions">
                <button
                    type="button"
                    className="project-action-btn primary"
                    onClick={onRefresh}
                    disabled={loading}
                >
                    {loading ? "Checking..." : "Refresh Projects"}
                </button>

                <a
                    className="project-action-btn secondary"
                    href={PUBLIC_GENERATOR_URL}
                    target="_blank"
                    rel="noreferrer"
                >
                    Open Generator
                </a>
            </div>
        </div>
    );
}

function ProjectActionCluster({
    project,
    isBusy,
    openPreview,
    handlePublish,
    handleUpdateLive
}) {
    const projectId = getProjectId(project);
    const liveUrl = getProjectLiveUrl(project);

    return (
        <div className="projects-action-cluster">
            <Link
                to={`/crm/projects/${projectId}`}
                className="project-action-btn compact"
                onClick={(event) => event.stopPropagation()}
            >
                <LayoutDashboard size={14} />
                CRM
            </Link>

            <button
                type="button"
                className="project-action-btn compact secondary"
                onClick={(event) => openPreview(project, event)}
            >
                <Eye size={14} />
                Preview
            </button>

            {liveUrl && (
                <a
                    href={liveUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="project-action-btn compact secondary"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Globe2 size={14} />
                    Live
                </a>
            )}

            {isPublishable(project) && (
                <button
                    type="button"
                    className="project-action-btn compact primary"
                    disabled={isBusy}
                    onClick={(event) => handlePublish(project, event)}
                >
                    <Rocket size={14} />
                    {isBusy ? "Publishing..." : "Publish"}
                </button>
            )}

            {isUpdatable(project) && (
                <button
                    type="button"
                    className="project-action-btn compact primary"
                    disabled={isBusy}
                    onClick={(event) => handleUpdateLive(project, event)}
                >
                    <RefreshCw size={14} />
                    {isBusy ? "Queuing..." : "Update"}
                </button>
            )}
        </div>
    );
}

function ProjectOwnerBadge({ project }) {
    const ownerType = getProjectOwnerType(project);

    const accountName =
        project?.accountName ||
        project?.AccountName ||
        project?.clientName ||
        project?.ClientName ||
        "";

    const parentName =
        project?.parentAccountName ||
        project?.ParentAccountName ||
        "";

    if (!accountName) {
        return (
            <div className="project-owner-badge unassigned">
                <span>Unassigned</span>
            </div>
        );
    }

    if (ownerType === "agency") {
        return (
            <div className="project-owner-badge agency">
                <span>{accountName}</span>
                <small>Agency workspace</small>
            </div>
        );
    }

    if (ownerType === "agency-client") {
        return (
            <div className="project-owner-badge agency-client">
                <span>{accountName}</span>
                <small>Client under {parentName}</small>
            </div>
        );
    }

    return (
        <div className="project-owner-badge direct-client">
            <span>{accountName}</span>
            <small>Direct client</small>
        </div>
    );
}

export default function ProjectsPage() {
    const [projects, setProjects] = useState([]);
    const [leads, setLeads] = useState([]);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [busyProjectId, setBusyProjectId] = useState("");
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [industryFilter, setIndustryFilter] = useState("all");
    const [ownerFilter, setOwnerFilter] = useState("all");
    const [agencyFilter, setAgencyFilter] = useState("all");
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const navigate = useNavigate();

    const platformUser = isPlatformUser();
    const agencyUser = isAgencyUser();
    const showProjectOwnerBadge = platformUser && !agencyUser;

    const leadCountByProject = useMemo(() => {
        return buildLeadCountMap(leads);
    }, [leads]);

    const agencyOptions = useMemo(() => {
        const map = new Map();

        (projects || []).forEach((project) => {
            const parentId = project?.parentAccountId || project?.ParentAccountId;
            const parentName = project?.parentAccountName || project?.ParentAccountName;

            const accountId = project?.accountId || project?.AccountId;
            const accountName = project?.accountName || project?.AccountName;
            const accountType = String(project?.accountType || project?.AccountType || "").toLowerCase();

            if (parentId && parentName) {
                map.set(String(parentId), parentName);
            }

            if (accountType === "agency" && accountId && accountName) {
                map.set(String(accountId), accountName);
            }
        });

        return Array.from(map.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [projects]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            setMessage("");

            const [projectsRes, leadsRes] = await Promise.allSettled([
                getCrmProjects(),
                getLeads()
            ]);

            if (projectsRes.status === "fulfilled") {
                setProjects(Array.isArray(projectsRes.value) ? projectsRes.value : []);
            } else {
                console.error("Projects load failed", projectsRes.reason);
                setProjects([]);
                setError(getErrorMessage(projectsRes.reason));
            }

            if (leadsRes.status === "fulfilled") {
                setLeads(Array.isArray(leadsRes.value) ? leadsRes.value : []);
            } else {
                console.error("Project lead counts load failed", leadsRes.reason);
                setLeads([]);
            }
        } catch (err) {
            console.error(err);
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const stats = useMemo(() => {
        const total = projects.length;

        const published = projects.filter((x) =>
            getDisplayStatus(x).toLowerCase() === "published"
        ).length;

        const generated = projects.filter((x) =>
            getDisplayStatus(x).toLowerCase() === "generated"
        ).length;

        const failed = projects.filter((x) =>
            getDisplayStatus(x).toLowerCase() === "failed"
        ).length;

        const totalLeads = leads.length;

        return {
            total,
            published,
            generated,
            failed,
            totalLeads
        };
    }, [projects, leads]);

    const industryOptions = useMemo(() => {
        const set = new Set();

        projects.forEach((project) => {
            const { industry } = getProjectIndustry(project);
            if (industry && industry !== "Unknown") {
                set.add(industry);
            }
        });

        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [projects]);

    const filteredProjects = useMemo(() => {
        const query = normalizeText(searchText);
        const selectedStatus = normalizeText(statusFilter);
        const selectedIndustry = normalizeText(industryFilter);

        return projects.filter((project) => {
            const projectName = getProjectName(project);
            const { industry, subIndustry } = getProjectIndustry(project);
            const status = getDisplayStatus(project);
            const liveUrl = getProjectLiveUrl(project);
            const lastUpdated = formatDateTime(getProjectLastUpdated(project));
            const ownerLabel = getProjectOwnerLabel(project);

            const searchable = normalizeText([
                projectName,
                industry,
                subIndustry,
                status,
                liveUrl,
                lastUpdated,
                ownerLabel,
                project?.accountName,
                project?.accountEmail,
                project?.accountType,
                project?.parentAccountName,
                project?.billingOwnerAccountName
            ].join(" "));

            const matchesSearch = !query || searchable.includes(query);

            const matchesStatus =
                selectedStatus === "all" ||
                normalizeText(status) === selectedStatus;

            const matchesIndustry =
                selectedIndustry === "all" ||
                normalizeText(industry) === selectedIndustry;

            const matchesOwner =
                !showProjectOwnerBadge ||
                ownerFilter === "all" ||
                getProjectOwnerType(project) === ownerFilter;

            const matchesAgency =
                !showProjectOwnerBadge ||
                agencyFilter === "all" ||
                getProjectAgencyId(project) === agencyFilter;

            return matchesSearch &&
                matchesStatus &&
                matchesIndustry &&
                matchesOwner &&
                matchesAgency;
        });
    }, [
        projects,
        searchText,
        statusFilter,
        industryFilter,
        ownerFilter,
        agencyFilter,
        showProjectOwnerBadge
    ]);

    const totalPages = Math.max(1, Math.ceil(filteredProjects.length / pageSize));

    const pagedProjects = useMemo(() => {
        const safePage = Math.min(page, totalPages);
        const start = (safePage - 1) * pageSize;

        return filteredProjects.slice(start, start + pageSize);
    }, [filteredProjects, page, pageSize, totalPages]);

    useEffect(() => {
        setPage(1);
    }, [searchText, statusFilter, industryFilter, ownerFilter, agencyFilter, pageSize]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const openPreview = (project, event) => {
        event.stopPropagation();

        const previewUrl = getProjectPreviewUrl(project);

        if (!previewUrl) {
            setError("Preview is not available for this project yet.");
            return;
        }

        window.open(previewUrl, "_blank", "noopener,noreferrer");
    };

    const handlePublish = async (project, event) => {
        event.stopPropagation();

        const projectId = getProjectId(project);
        if (!projectId) return;

        try {
            setBusyProjectId(projectId);
            setError("");
            setMessage("");

            const res = await publishCrmProject(projectId);

            setMessage(
                res?.liveUrl
                    ? "Project published successfully."
                    : "Publish request completed."
            );

            await load();
        } catch (err) {
            console.error(err);
            setError(getErrorMessage(err, "Failed to publish project."));
        } finally {
            setBusyProjectId("");
        }
    };

    const handleUpdateLive = async (project, event) => {
        event.stopPropagation();

        const projectId = getProjectId(project);
        if (!projectId) return;

        try {
            setBusyProjectId(projectId);
            setError("");
            setMessage("");

            await redeployProject(projectId, "manual-project-list");

            setMessage("Live site update has been queued.");
            await load();
        } catch (err) {
            console.error(err);
            setError(getErrorMessage(err, "Failed to update live site."));
        } finally {
            setBusyProjectId("");
        }
    };

    const resetFilters = () => {
        setSearchText("");
        setStatusFilter("all");
        setIndustryFilter("all");
        setOwnerFilter("all");
        setAgencyFilter("all");
        setPage(1);
    };

    const firstVisible = filteredProjects.length === 0
        ? 0
        : (page - 1) * pageSize + 1;

    const lastVisible = Math.min(page * pageSize, filteredProjects.length);

    const filtersActive =
        searchText ||
        statusFilter !== "all" ||
        industryFilter !== "all" ||
        ownerFilter !== "all" ||
        agencyFilter !== "all";

    return (
        <PageShell title="Projects">
            <div className="projects-page-shell">
                <section className="projects-command-card">
                    <div>
                        <span className="project-kicker">Website CRM</span>
                        <h2>Generated Website Projects</h2>
                        <p>
                            Manage generated websites, CRM modules, lead flow, deployment status,
                            live URLs, preview, publishing, and update operations.
                        </p>
                    </div>

                    <div className="projects-command-actions">
                        <a
                            className="project-action-btn secondary"
                            href={PUBLIC_GENERATOR_URL}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Open Generator
                        </a>

                        <button
                            type="button"
                            className="project-action-btn primary"
                            onClick={load}
                            disabled={loading}
                        >
                            <RefreshCw size={16} />
                            {loading ? "Refreshing..." : "Refresh Projects"}
                        </button>
                    </div>
                </section>

                <section className="projects-stats-grid">
                    <div className="projects-stat-card">
                        <span>Total Projects</span>
                        <strong>{stats.total}</strong>
                        <small>Current access scope</small>
                    </div>

                    <div className="projects-stat-card">
                        <span>Published</span>
                        <strong>{stats.published}</strong>
                        <small>Have live website URL</small>
                    </div>

                    <div className="projects-stat-card">
                        <span>Generated</span>
                        <strong>{stats.generated}</strong>
                        <small>Ready to publish</small>
                    </div>

                    <div className="projects-stat-card">
                        <span>Leads</span>
                        <strong>{stats.totalLeads}</strong>
                        <small>Total project enquiries</small>
                    </div>
                </section>

                {message && (
                    <section className="projects-message-card success">
                        <strong>Success</strong>
                        <p>{message}</p>
                    </section>
                )}

                {error && (
                    <section className="projects-error-card">
                        <strong>Could not complete request</strong>
                        <p>{String(error)}</p>

                        <button
                            type="button"
                            className="project-action-btn secondary"
                            onClick={load}
                            disabled={loading}
                        >
                            Try Again
                        </button>
                    </section>
                )}

                <section className="projects-list-panel">
                    <div className="projects-panel-header">
                        <div>
                            <span className="project-kicker">Project Library</span>
                            <h3>My Projects</h3>
                            <p>
                                Open a project to edit CRM modules, preview the generated website,
                                publish, update live site, or review leads.
                            </p>
                        </div>
                    </div>

                    {loading && projects.length === 0 ? (
                        <div className="projects-loading-state">
                            Loading projects...
                        </div>
                    ) : !loading && projects.length === 0 && !error ? (
                        <ProjectEmptyState onRefresh={load} loading={loading} />
                    ) : (
                        <>
                            <div className="projects-filter-bar">
                                <div className="projects-search-box">
                                    <Search size={17} />
                                    <input
                                        value={searchText}
                                        onChange={(event) => setSearchText(event.target.value)}
                                        placeholder="Search projects, owner, agency, industry, status, live URL..."
                                    />
                                </div>

                                <div className="projects-filter-group">
                                    <label>
                                        <SlidersHorizontal size={15} />
                                        <select
                                            value={statusFilter}
                                            onChange={(event) => setStatusFilter(event.target.value)}
                                        >
                                            <option value="all">All statuses</option>
                                            <option value="Draft">Draft</option>
                                            <option value="Generating">Generating</option>
                                            <option value="Generated">Generated</option>
                                            <option value="Published">Published</option>
                                            <option value="Failed">Failed</option>
                                        </select>
                                    </label>

                                    <label>
                                        <select
                                            value={industryFilter}
                                            onChange={(event) => setIndustryFilter(event.target.value)}
                                        >
                                            <option value="all">All industries</option>
                                            {industryOptions.map((industry) => (
                                                <option key={industry} value={industry}>
                                                    {industry}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    {showProjectOwnerBadge && (
                                        <>
                                            <label>
                                                <select
                                                    value={ownerFilter}
                                                    onChange={(event) => setOwnerFilter(event.target.value)}
                                                >
                                                    <option value="all">All owners</option>
                                                    <option value="direct-client">Direct clients</option>
                                                    <option value="agency-client">Agency clients</option>
                                                    <option value="agency">Agency workspaces</option>
                                                    <option value="unassigned">Unassigned</option>
                                                </select>
                                            </label>

                                            <label>
                                                <select
                                                    value={agencyFilter}
                                                    onChange={(event) => setAgencyFilter(event.target.value)}
                                                >
                                                    <option value="all">All agencies</option>
                                                    {agencyOptions.map((agency) => (
                                                        <option key={agency.id} value={agency.id}>
                                                            {agency.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </>
                                    )}

                                    <label>
                                        <select
                                            value={pageSize}
                                            onChange={(event) => setPageSize(Number(event.target.value))}
                                        >
                                            {PAGE_SIZE_OPTIONS.map((size) => (
                                                <option key={size} value={size}>
                                                    {size} / page
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <button
                                        type="button"
                                        className="project-action-btn compact secondary"
                                        onClick={resetFilters}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>

                            <div className="projects-result-summary">
                                <span>
                                    Showing {firstVisible}-{lastVisible} of {filteredProjects.length} projects
                                </span>

                                {filtersActive && (
                                    <strong>
                                        Filtered from {projects.length} total
                                    </strong>
                                )}
                            </div>

                            {filteredProjects.length === 0 ? (
                                <div className="projects-filter-empty">
                                    <div className="projects-empty-icon">🔎</div>
                                    <h3>No matching projects</h3>
                                    <p>
                                        Try changing the search text, status, owner, agency, or industry filter.
                                    </p>
                                    <button
                                        type="button"
                                        className="project-action-btn secondary"
                                        onClick={resetFilters}
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="projects-desktop-table">
                                        <div className="variant-table-wrap">
                                            <table className="variant-table projects-table projects-ops-table">
                                                <thead>
                                                    <tr>
                                                        <th>Project</th>
                                                        <th>Industry</th>
                                                        <th>Status</th>
                                                        <th>Live URL</th>
                                                        <th>Last Updated</th>
                                                        <th>Leads</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {pagedProjects.map((project) => {
                                                        const projectId = getProjectId(project);
                                                        const projectName = getProjectName(project);
                                                        const { industry, subIndustry } = getProjectIndustry(project);
                                                        const status = getDisplayStatus(project);
                                                        const liveUrl = getProjectLiveUrl(project);
                                                        const leadCount = getProjectLeadCountFromMap(project, leadCountByProject);
                                                        const lastUpdated = getProjectLastUpdated(project);
                                                        const isBusy = busyProjectId === projectId;

                                                        return (
                                                            <tr
                                                                key={projectId}
                                                                onClick={() => navigate(`/crm/projects/${projectId}`)}
                                                                className="projects-row-clickable"
                                                            >
                                                                <td>
                                                                    <div className="projects-business-cell">
                                                                        <strong>{projectName}</strong>
                                                                        <span>{subIndustry || "Generated website"}</span>
                                                                        {showProjectOwnerBadge && (
                                                                            <ProjectOwnerBadge project={project} />
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div className="projects-industry-cell">
                                                                        <strong>{industry}</strong>
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <span className={`projects-status-pill ${getStatusClass(status)}`}>
                                                                        {status}
                                                                    </span>
                                                                </td>

                                                                <td>
                                                                    {liveUrl ? (
                                                                        <a
                                                                            href={liveUrl}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="projects-live-link"
                                                                            onClick={(event) => event.stopPropagation()}
                                                                        >
                                                                            View Live <ExternalLink size={13} />
                                                                        </a>
                                                                    ) : (
                                                                        <span className="projects-muted">Not published</span>
                                                                    )}
                                                                </td>

                                                                <td>
                                                                    <span className="projects-muted">
                                                                        {formatDateTime(lastUpdated)}
                                                                    </span>
                                                                </td>

                                                                <td>
                                                                    <span className="projects-lead-pill">
                                                                        {leadCount}
                                                                    </span>
                                                                </td>

                                                                <td>
                                                                    <ProjectActionCluster
                                                                        project={project}
                                                                        isBusy={isBusy}
                                                                        openPreview={openPreview}
                                                                        handlePublish={handlePublish}
                                                                        handleUpdateLive={handleUpdateLive}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="projects-mobile-list">
                                        {pagedProjects.map((project) => {
                                            const projectId = getProjectId(project);
                                            const projectName = getProjectName(project);
                                            const { industry, subIndustry } = getProjectIndustry(project);
                                            const status = getDisplayStatus(project);
                                            const leadCount = getProjectLeadCountFromMap(project, leadCountByProject);
                                            const lastUpdated = getProjectLastUpdated(project);
                                            const isBusy = busyProjectId === projectId;

                                            return (
                                                <article
                                                    key={projectId}
                                                    className="projects-mobile-card"
                                                    onClick={() => navigate(`/crm/projects/${projectId}`)}
                                                >
                                                    <div className="projects-mobile-card-head">
                                                        <div>
                                                            <span className="project-kicker">{industry}</span>
                                                            <h4>{projectName}</h4>
                                                            <p>{subIndustry || "Generated website"}</p>
                                                            <small className="project-owner-line">
                                                                {getProjectOwnerLabel(project)}
                                                            </small>
                                                        </div>

                                                        <span className={`projects-status-pill ${getStatusClass(status)}`}>
                                                            {status}
                                                        </span>
                                                    </div>

                                                    <div className="projects-mobile-meta">
                                                        <div>
                                                            <span>Leads</span>
                                                            <strong>{leadCount}</strong>
                                                        </div>

                                                        <div>
                                                            <span>Updated</span>
                                                            <strong>{formatDateTime(lastUpdated)}</strong>
                                                        </div>
                                                    </div>

                                                    <div className="projects-mobile-actions">
                                                        <ProjectActionCluster
                                                            project={project}
                                                            isBusy={isBusy}
                                                            openPreview={openPreview}
                                                            handlePublish={handlePublish}
                                                            handleUpdateLive={handleUpdateLive}
                                                        />
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>

                                    <div className="projects-pagination">
                                        <div>
                                            <span>
                                                Page {page} of {totalPages}
                                            </span>
                                            <strong>
                                                {filteredProjects.length} result{filteredProjects.length === 1 ? "" : "s"}
                                            </strong>
                                        </div>

                                        <div className="projects-pagination-actions">
                                            <button
                                                type="button"
                                                className="project-action-btn compact secondary"
                                                disabled={page <= 1}
                                                onClick={() => setPage((value) => Math.max(1, value - 1))}
                                            >
                                                Previous
                                            </button>

                                            <button
                                                type="button"
                                                className="project-action-btn compact secondary"
                                                disabled={page >= totalPages}
                                                onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </section>
            </div>
        </PageShell>
    );
}